import React, { useMemo } from "react";

import { LeafIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import * as ProviderLogos from "Components/icons/providers/index";
import { capitalize } from "Libs/utils";
import { selectGreenDiscountEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { regionSelector } from "Reducers/project/region";
import { useAppSelector } from "Store/hooks";

import * as S from "./EnvironmentImpact.styles";

export const getLeafColor = carbonIntensity => {
  return carbonIntensity > 500
    ? "black"
    : carbonIntensity > 300
      ? "red"
      : carbonIntensity > 200
        ? "orange"
        : carbonIntensity > 100
          ? "yellow"
          : "green";
};

const EnvironmentImpact = ({
  inModal = false,
  regionId,
  shouldShowAllLeafs = false
}: {
  inModal?: boolean;
  regionId?: string;
  shouldShowAllLeafs?: boolean;
}) => {
  const intl = useIntl();

  const region = useAppSelector(state => regionSelector(state, { regionId }));
  const isGreenDiscountEnabled = useAppSelector(selectGreenDiscountEnabled);
  const regionColor = getLeafColor(
    region?.environmental_impact?.carbon_intensity
  );
  const leafColor = {
    green: "--java",
    yellow: "--gold",
    orange: "--orange",
    red: "--red",
    black: "--night"
  }[regionColor];
  const isGreen = region?.environmental_impact?.green;

  // The purpose of this test is to prevent the display of details
  // when we do not have all the data. On the staging for example,
  // we don't have all the properties for all the regions.
  // And on the webpass, the property "provider" does not exist
  const hasDatas = useMemo(() => {
    return (
      region?.provider?.name &&
      region?.environmental_impact?.carbon_intensity &&
      region?.datacenter?.location
    );
  }, [region]);

  const ProviderLogo = useMemo(() => {
    if (hasDatas)
      return ProviderLogos[
        capitalize(region?.provider.name.toLowerCase() ?? "")
      ];
  }, [hasDatas, region]);

  const leafFill = useMemo(() => {
    if (hasDatas && leafColor) return leafColor;
    return "--granite";
  }, [hasDatas, leafColor]);

  return (
    <S.Wrapper>
      {!inModal &&
        (shouldShowAllLeafs ? (
          <S.Icon>
            <LeafIcon fill={`var(${leafFill})`} width={16} height={16} />
          </S.Icon>
        ) : (
          isGreen && (
            <S.Icon>
              <LeafIcon fill={`var(${leafFill})`} width={16} height={16} />
            </S.Icon>
          )
        ))}
      {hasDatas ? (
        <S.Details>
          <S.Logo>
            {ProviderLogo ? (
              <ProviderLogo />
            ) : (
              <img
                src={`data:image/svg+xml;base64,${region?.provider?.logo}`}
                alt={region?.provider.name}
              />
            )}
          </S.Logo>
          <S.CarbonIntensity>
            {intl.formatMessage(
              { id: "environment_impact.carbon_intensity" },
              {
                carbonIntensity: region?.environmental_impact?.carbon_intensity
              }
            )}
          </S.CarbonIntensity>
          <S.Label>
            {intl.formatMessage({ id: "environment_impact.label" })}
          </S.Label>
          <S.Location>{region?.datacenter?.location}</S.Location>
          {isGreenDiscountEnabled && isGreen && (
            <S.DefaultGreen>
              {intl.formatMessage({ id: "environment_impact.green" })}
            </S.DefaultGreen>
          )}
        </S.Details>
      ) : (
        <S.DefaultWrapper>
          <S.Default>
            {intl.formatMessage({ id: "environment_impact.default" })}
          </S.Default>
          {isGreenDiscountEnabled && (
            <S.DefaultGreen>
              {intl.formatMessage({ id: "environment_impact.default.green" })}
            </S.DefaultGreen>
          )}
        </S.DefaultWrapper>
      )}
    </S.Wrapper>
  );
};

export default EnvironmentImpact;
